import { CSSProperties } from 'react';
import cx from 'classnames';

export interface SliderProps {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  /** Read by screen readers; the visible caption is the caller's. */
  label: string;
  /** `sm` a 4px track for a side panel, `md` an 8px track in a window. */
  size?: 'sm' | 'md';
  /** `neutral` where red would read as a warning — a tool's own settings. */
  tone?: 'primary' | 'neutral';
  disabled?: boolean;
  className?: string;
}

const thumbClasses = {
  sm: '[&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4',
  md: '[&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5',
};

const toneClasses = {
  primary: '[&::-webkit-slider-thumb]:bg-primary [&::-moz-range-thumb]:bg-primary',
  neutral: '[&::-webkit-slider-thumb]:bg-ink [&::-moz-range-thumb]:bg-ink',
};

/** A number on a track. The input itself is the track; the part up to the thumb is filled. */
export const Slider = ({
  value,
  onChange,
  min,
  max,
  step = 1,
  label,
  size = 'md',
  tone = 'primary',
  disabled,
  className,
}: SliderProps) => {
  const filled = max > min ? ((value - min) / (max - min)) * 100 : 0;
  const fill = disabled ? 'var(--color-ink-subtle)' : tone === 'primary' ? 'var(--color-primary)' : 'var(--color-ink)';
  const style: CSSProperties = {
    background: `linear-gradient(to right, ${fill} ${filled}%, var(--color-line-strong) ${filled}%)`,
  };

  return (
    <input
      type="range"
      aria-label={label}
      value={value}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      onChange={(e) => onChange(Number(e.target.value))}
      style={style}
      className={cx(
        'w-full cursor-pointer appearance-none rounded-full disabled:cursor-not-allowed',
        size === 'sm' ? 'h-1' : 'h-2',
        '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-surface [&::-webkit-slider-thumb]:shadow-raised',
        '[&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-surface [&::-moz-range-thumb]:shadow-raised',
        'focus-visible:outline-none focus-visible:[&::-webkit-slider-thumb]:ring-2 focus-visible:[&::-webkit-slider-thumb]:ring-primary focus-visible:[&::-moz-range-thumb]:ring-2 focus-visible:[&::-moz-range-thumb]:ring-primary',
        thumbClasses[size],
        disabled ? '[&::-webkit-slider-thumb]:bg-ink-subtle [&::-moz-range-thumb]:bg-ink-subtle' : toneClasses[tone],
        className,
      )}
    />
  );
};
